import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Search, Download, RefreshCw, Loader2, ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const fmtDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const PAGE_SIZE = 50;

const SummaryCard = ({ label, value, accent, testid }) => (
  <div className="bg-white rounded-lg border border-[#E5DFD0] p-4" data-testid={testid}>
    <p className="text-xs text-[#5A5F6E] leading-tight">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${accent}`}>{(value ?? 0).toLocaleString()}</p>
  </div>
);

const InWIPBundleStatusReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [pendingOnly, setPendingOnly] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exporting, setExporting] = useState(false);
  const [returnTarget, setReturnTarget] = useState(null); // { bundle_code, pending, design_name, sku_code }
  const [returnNewCode, setReturnNewCode] = useState('');
  const [returnReason, setReturnReason] = useState('');
  const [returnSubmitting, setReturnSubmitting] = useState(false);
  const debounceRef = useRef(null);
  const reqSeqRef = useRef(0);

  const fetchReport = useCallback(async () => {
    const seq = ++reqSeqRef.current;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('page_size', String(PAGE_SIZE));
      if (search) params.append('search', search);
      if (pendingOnly) params.append('pending_only', 'true');
      if (dateFrom) params.append('picked_date_from', dateFrom);
      if (dateTo) params.append('picked_date_to', dateTo);
      const res = await apiClient.get(`/wms/sfg/in-wip-bundle-status?${params.toString()}`);
      if (seq !== reqSeqRef.current) return; // a newer request superseded this one
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
      setSummary(res.data.summary || null);
    } catch (err) {
      if (seq !== reqSeqRef.current) return;
      console.error(err);
      toast.error('Failed to load IN-WIP bundle status report');
      setRows([]);
      setTotal(0);
    } finally {
      if (seq === reqSeqRef.current) setLoading(false);
    }
  }, [page, search, pendingOnly, dateFrom, dateTo]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Debounce the search box
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 400);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [searchInput]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (pendingOnly) params.append('pending_only', 'true');
      if (dateFrom) params.append('picked_date_from', dateFrom);
      if (dateTo) params.append('picked_date_to', dateTo);
      const res = await apiClient.get(
        `/wms/sfg/in-wip-bundle-status/export?${params.toString()}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `in_wip_bundle_status_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      toast.success('Report exported');
    } catch (err) {
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const openReturnDialog = (row) => {
    setReturnTarget({
      bundle_code: row.bundle_code,
      pending: (row.pending_on_floor || 0) + (row.label_printed_not_putaway || 0),
      design_name: row.design_name,
      sku_code: row.sku_code,
    });
    setReturnNewCode('');
    setReturnReason('');
  };

  const submitReturnToSfg = async () => {
    if (!returnTarget) return;
    const newCode = returnNewCode.trim().toUpperCase();
    if (!newCode) {
      toast.error('Scan the NEW bundle sticker code');
      return;
    }
    if (newCode === returnTarget.bundle_code) {
      toast.error('New bundle code must differ from the old one');
      return;
    }
    setReturnSubmitting(true);
    try {
      const res = await apiClient.post('/wms/sfg/in-wip/return-to-sfg', {
        old_bundle_code: returnTarget.bundle_code,
        new_bundle_code: newCode,
        reason: returnReason.trim() || null,
      });
      toast.success(res.data?.message || `Returned to SFG as ${newCode}`);
      setReturnTarget(null);
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to return bundle to SFG');
    } finally {
      setReturnSubmitting(false);
    }
  };

  const numCell = (v, cls = '') => (
    <td className={`px-3 py-2.5 text-sm text-center tabular-nums ${cls}`}>{v || 0}</td>
  );

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms/sfg')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="inwip-status-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold font-display">IN-WIP Bundle Status</h1>
                <p className="text-sm text-[#E8DCC8]">Per-bundle USN accounting for bundles in WIP</p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={fetchReport}
              variant="outline"
              className="bg-white/10 text-white border-white/20 hover:bg-white/20"
              data-testid="inwip-status-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={handleExport}
              disabled={exporting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="inwip-status-export-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Export CSV
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 md:px-6 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6" data-testid="inwip-status-summary">
          <SummaryCard label="Bundles" value={summary?.total_bundles} accent="text-[#1A1F2E]" testid="summary-bundles" />
          <SummaryCard label="Total USNs" value={summary?.total_usns} accent="text-blue-600" testid="summary-total-usns" />
          <SummaryCard label="Put Away (FG)" value={summary?.put_away_fg} accent="text-emerald-600" testid="summary-fg" />
          <SummaryCard label="Label Printed, Not Put Away" value={summary?.label_printed_not_putaway} accent="text-amber-600" testid="summary-label" />
          <SummaryCard label="Consumed (Combo)" value={summary?.consumed_combo} accent="text-purple-600" testid="summary-combo" />
          <SummaryCard label="Damaged" value={summary?.damaged} accent="text-red-600" testid="summary-damaged" />
          <SummaryCard label="Returned to SFG" value={summary?.returned_to_sfg} accent="text-cyan-600" testid="summary-returned" />
          <SummaryCard label="Pending on Floor" value={summary?.pending_on_floor} accent="text-orange-600" testid="summary-pending" />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-4 mb-6 flex flex-col md:flex-row md:items-end gap-4">
          <div className="flex-1">
            <Label>Search (Bundle / SKU / Design)</Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search bundle code, SKU or design..."
                className="pl-10"
                data-testid="inwip-status-search"
              />
            </div>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <Label htmlFor="picked-from">Picked From</Label>
              <Input
                id="picked-from"
                type="date"
                value={dateFrom}
                onChange={(e) => { setPage(1); setDateFrom(e.target.value); }}
                className="mt-1 w-40"
                data-testid="inwip-status-date-from"
              />
            </div>
            <div>
              <Label htmlFor="picked-to">Picked To</Label>
              <Input
                id="picked-to"
                type="date"
                value={dateTo}
                onChange={(e) => { setPage(1); setDateTo(e.target.value); }}
                className="mt-1 w-40"
                data-testid="inwip-status-date-to"
              />
            </div>
            {(dateFrom || dateTo) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setPage(1); setDateFrom(''); setDateTo(''); }}
                className="mb-0.5 text-[#5A5F6E]"
                data-testid="inwip-status-date-clear"
              >
                Clear
              </Button>
            )}
          </div>
          <div className="flex items-center gap-3 pb-2">
            <Switch
              id="pending-only"
              checked={pendingOnly}
              onCheckedChange={(v) => { setPage(1); setPendingOnly(v); }}
              data-testid="inwip-status-pending-toggle"
            />
            <Label htmlFor="pending-only" className="cursor-pointer">Only bundles with pieces pending</Label>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#2D5F4F]" />
              <span className="ml-3 text-[#5A5F6E]">Loading report...</span>
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-20">
              <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-lg text-[#5A5F6E]">No IN-WIP bundles found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="inwip-status-table">
                <thead className="bg-[#F5F1E8] sticky top-0">
                  <tr>
                    <th className="px-3 py-3 text-left font-semibold text-[#1A1F2E] whitespace-nowrap">Bundle Code</th>
                    <th className="px-3 py-3 text-left font-semibold text-[#1A1F2E] whitespace-nowrap">SKU Code</th>
                    <th className="px-3 py-3 text-left font-semibold text-[#1A1F2E] whitespace-nowrap">Design</th>
                    <th className="px-3 py-3 text-left font-semibold text-[#1A1F2E] whitespace-nowrap">Shade</th>
                    <th className="px-3 py-3 text-left font-semibold text-[#1A1F2E] whitespace-nowrap">Pick Slip</th>
                    <th className="px-3 py-3 text-left font-semibold text-[#1A1F2E] whitespace-nowrap">Handover Person</th>
                    <th className="px-3 py-3 text-left font-semibold text-[#1A1F2E] whitespace-nowrap">Picked Date</th>
                    <th className="px-3 py-3 text-center font-semibold text-[#1A1F2E]">Total USNs</th>
                    <th className="px-3 py-3 text-center font-semibold text-emerald-700">Put Away (FG)</th>
                    <th className="px-3 py-3 text-center font-semibold text-amber-700">Label Printed,<br/>Not Put Away</th>
                    <th className="px-3 py-3 text-center font-semibold text-purple-700">Consumed<br/>(Combo)</th>
                    <th className="px-3 py-3 text-center font-semibold text-red-700">Damaged</th>
                    <th className="px-3 py-3 text-center font-semibold text-cyan-700">Returned<br/>to SFG</th>
                    <th className="px-3 py-3 text-center font-semibold text-orange-700">Pending<br/>on Floor</th>
                    <th className="px-3 py-3 text-center font-semibold text-[#1A1F2E] whitespace-nowrap">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5DFD0]">
                  {rows.map((row, idx) => (
                    <tr key={row.bundle_code + idx} className="hover:bg-[#FFFBF5]" data-testid={`inwip-row-${row.bundle_code}`}>
                      <td className="px-3 py-2.5 font-medium text-[#1A1F2E] whitespace-nowrap font-mono">{row.bundle_code}</td>
                      <td className="px-3 py-2.5 text-[#5A5F6E] whitespace-nowrap">{row.sku_code || '-'}</td>
                      <td className="px-3 py-2.5 text-[#5A5F6E] whitespace-nowrap">{row.design_name || '-'}</td>
                      <td className="px-3 py-2.5 text-[#5A5F6E] whitespace-nowrap">{row.shade || '-'}</td>
                      <td className="px-3 py-2.5 text-[#5A5F6E] whitespace-nowrap font-mono text-xs">{row.pick_slip_no || '-'}</td>
                      <td className="px-3 py-2.5 text-[#5A5F6E] whitespace-nowrap" data-testid={`inwip-handover-${row.bundle_code}`}>{row.handover_by || '-'}</td>
                      <td className="px-3 py-2.5 text-[#5A5F6E] whitespace-nowrap text-xs">{fmtDate(row.picked_at)}</td>
                      {numCell(row.total_usns, 'font-semibold text-[#1A1F2E]')}
                      {numCell(row.put_away_fg, 'text-emerald-700')}
                      {numCell(row.label_printed_not_putaway, 'text-amber-700')}
                      {numCell(row.consumed_combo, 'text-purple-700')}
                      {numCell(row.damaged, 'text-red-700')}
                      {numCell(row.returned_to_sfg, 'text-cyan-700')}
                      {numCell(row.pending_on_floor, 'text-orange-700 font-semibold')}
                      <td className="px-3 py-2.5 text-center whitespace-nowrap">
                        {((row.pending_on_floor || 0) + (row.label_printed_not_putaway || 0)) > 0 ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-cyan-600 text-cyan-700 hover:bg-cyan-50 h-8"
                            onClick={() => openReturnDialog(row)}
                            data-testid={`inwip-return-to-sfg-${row.bundle_code}`}
                          >
                            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Return to SFG
                          </Button>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {summary && (
                  <tfoot className="bg-[#1A1F2E] text-white font-semibold" data-testid="inwip-status-totals-row">
                    <tr>
                      <td className="px-3 py-3" colSpan={7}>TOTALS ({(summary.total_bundles || 0).toLocaleString()} bundles{search || pendingOnly || dateFrom || dateTo ? ', filtered' : ''})</td>
                      <td className="px-3 py-3 text-center tabular-nums">{(summary.total_usns || 0).toLocaleString()}</td>
                      <td className="px-3 py-3 text-center tabular-nums">{(summary.put_away_fg || 0).toLocaleString()}</td>
                      <td className="px-3 py-3 text-center tabular-nums">{(summary.label_printed_not_putaway || 0).toLocaleString()}</td>
                      <td className="px-3 py-3 text-center tabular-nums">{(summary.consumed_combo || 0).toLocaleString()}</td>
                      <td className="px-3 py-3 text-center tabular-nums">{(summary.damaged || 0).toLocaleString()}</td>
                      <td className="px-3 py-3 text-center tabular-nums">{(summary.returned_to_sfg || 0).toLocaleString()}</td>
                      <td className="px-3 py-3 text-center tabular-nums">{(summary.pending_on_floor || 0).toLocaleString()}</td>
                      <td className="px-3 py-3"></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-[#5A5F6E]" data-testid="inwip-status-page-info">
              Page {page} of {totalPages} · {total.toLocaleString()} bundle(s)
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                data-testid="inwip-status-prev"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                data-testid="inwip-status-next"
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Return to SFG dialog — scan the new (pre-printed) bundle sticker code */}
      <Dialog open={!!returnTarget} onOpenChange={(o) => { if (!o) setReturnTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="inwip-return-to-sfg-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-cyan-700" /> Return Bundle to SFG
            </DialogTitle>
          </DialogHeader>
          {returnTarget && (
            <div className="space-y-4">
              <div className="rounded-lg bg-[#F5F1E8] border border-[#E5DFD0] p-3 text-sm">
                <div className="flex justify-between py-0.5">
                  <span className="text-[#5A5F6E]">Old bundle</span>
                  <span className="font-mono font-semibold">{returnTarget.bundle_code}</span>
                </div>
                <div className="flex justify-between py-0.5">
                  <span className="text-[#5A5F6E]">Design / SKU</span>
                  <span className="font-medium">{returnTarget.design_name || '-'} · {returnTarget.sku_code || '-'}</span>
                </div>
                <div className="flex justify-between py-0.5">
                  <span className="text-[#5A5F6E]">Pending pieces to return</span>
                  <span className="font-bold text-orange-700">{returnTarget.pending}</span>
                </div>
              </div>
              <div>
                <Label htmlFor="return-new-code" className="text-sm">Scan NEW bundle sticker code</Label>
                <Input
                  id="return-new-code"
                  autoFocus
                  value={returnNewCode}
                  onChange={(e) => setReturnNewCode(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') submitReturnToSfg(); }}
                  placeholder="e.g. BUN012345/26-27"
                  className="font-mono mt-1"
                  data-testid="inwip-return-new-code-input"
                />
                <p className="text-xs text-[#5A5F6E] mt-1">
                  Paste a fresh sticker (printed from Print Codes) on the bundle and scan it. The pending pieces
                  will be staged as <b>Pending Put Away</b>, ready for the regular SFG Put-Away.
                </p>
              </div>
              <div>
                <Label htmlFor="return-reason" className="text-sm">Reason (optional)</Label>
                <Input
                  id="return-reason"
                  value={returnReason}
                  onChange={(e) => setReturnReason(e.target.value)}
                  placeholder="Why is this being returned?"
                  className="mt-1"
                  data-testid="inwip-return-reason-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnTarget(null)} disabled={returnSubmitting} data-testid="inwip-return-cancel-btn">
              Cancel
            </Button>
            <Button
              className="bg-cyan-700 hover:bg-cyan-800 text-white"
              onClick={submitReturnToSfg}
              disabled={returnSubmitting || !returnNewCode.trim()}
              data-testid="inwip-return-submit-btn"
            >
              {returnSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
              Return to SFG
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InWIPBundleStatusReport;
